import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { query } from "@/services/services";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Panel, PanelHeader } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { QuickAddDialogs } from "@/components/shell/QuickAddDialogs";
import { AISimPanel } from "@/components/matter/AISimPanel";
import { ProceedingsTab } from "@/components/matter/ProceedingsTab";
import { MatterClosure } from "@/components/matter/MatterClosure";
import { CasePassport } from "@/components/matter/CasePassport";
import { useSnapshot, AttentionBar, SnapshotCommandCentre } from "@/components/matter/SnapshotCommandCentre";
import { DocClassificationPanel } from "@/components/matter/DocClassificationPanel";
import { EvidenceTab } from "@/components/matter/EvidenceTab";
import { OrderAgentPanel } from "@/components/matter/OrderAgentPanel";
import { CauseTitleBuilder } from "@/components/matter/CauseTitleBuilder";
import { IntelInbox } from "@/components/matter/IntelInbox";
import { ChainOfTitlePanel } from "@/components/matter/ChainOfTitlePanel";
import axios from "axios";
import { Lock, Sparkles } from "lucide-react";
import { AIChat } from "@/components/common/AIChat";
import { DocumentOCR } from "@/components/common/DocumentOCR";
import { LimitationClockMark } from "@/components/common/LimitationClockMark";
import { LimitationBadge } from "@/components/common/LimitationBadge";
import { FinanceWorkspace } from "@/components/finance/FinanceWorkspace";
import { useFinanceTotals, money as ledgerMoney } from "@/components/finance/financeApi";
import { ProofGraphTab } from "@/components/proofgraph/ProofGraphTab";
import { MediaPreview, kindIcon } from "@/components/common/MediaPreview";
import { AttachmentPanel } from "@/components/common/AttachmentPanel";
import { formatDate, formatDateTime, fromNow, daysUntil, formatMoney, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion, LayoutGroup } from "framer-motion";
import { SPRING } from "@/motion";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Plus, Upload, Gavel, PenLine, BookOpen, StickyNote, Receipt, MoreVertical,
  Calendar as CalIcon, User, Scale, FileText, Pin, Calculator, GitBranch, Download,
} from "lucide-react";

const TABS = ["Snapshot", "Record", "Proceedings", "ProofGraph", "Hearings", "Tasks", "Docs & Exhibits", "Evidence", "Parties", "AI", "Billing", "Notes", "Communication", "Orders & Compliance", "Timeline", "Court Fee", "Strategy", "Research", "Drafts"];
const NAV_GROUPS = [
  { label: "Case", tabs: ["Snapshot", "Record", "Parties", "Proceedings", "Timeline"] },
  { label: "Legal Work", tabs: ["Docs & Exhibits", "Evidence", "ProofGraph", "Research", "Drafts", "Hearings", "Orders & Compliance"] },
  { label: "Operations", tabs: ["Tasks", "Communication", "Billing", "Court Fee"] },
  { label: "Private", tabs: ["Notes", "Strategy", "AI"] },
];
const TAB_LABEL = { "Docs & Exhibits": "Docs", "Orders & Compliance": "Orders", Communication: "Communications" };

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MatterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { db, update, isClient, clientScopeId, personaRole, can, ensureMatterData } = useApp();
  const financeTotals = useFinanceTotals({ matterId: id });
  const m = query.matter(db, id);
  useEffect(() => { ensureMatterData(id); }, [id]); // eslint-disable-line
  const [quick, setQuick] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [closureAction, setClosureAction] = useState(() => searchParams.get("action") === "close" ? "close" : null);
  const [tab, setTab] = useState(() => (TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "Snapshot"));
  const [proofIntent, setProofIntent] = useState(null);
  const [limCheck, setLimCheck] = useState(0);
  const [snap, reloadSnap] = useSnapshot(id);
  const changeTab = (t) => { setTab(t); setSearchParams(t === "Snapshot" ? {} : { tab: t }, { replace: true }); if (t === "Snapshot") reloadSnap(); };
  const showMissing = () => { changeTab("ProofGraph"); setProofIntent({ type: "gaps", ts: Date.now() }); };

  if (!m) return <EmptyState icon={FileText} title="Matter not found" message="This matter may have been archived or removed." actionLabel="Back to Matters" onAction={() => navigate("/matters")} />;
  if (isClient && m.clientId !== clientScopeId) return <EmptyState icon={FileText} title="Restricted matter" message="You do not have access to this matter." actionLabel="Back to Matters" onAction={() => navigate("/matters")} />;

  const visibleTabs = isClient ? TABS.filter((t) => !["AI", "Notes", "Strategy", "Communication", "ProofGraph", "Proceedings", "Research", "Drafts", "Evidence"].includes(t)) : TABS.filter((t) => t !== "Evidence" || !["Finance", "Accounts", "Billing"].includes(personaRole));

  const client = query.client(db, m.clientId);
  const tasks = query.tasksFor(db, id);
  const docs = query.docsFor(db, id);
  const hearings = query.hearingsFor(db, id).sort((a, b) => new Date(b.date) - new Date(a.date));
  const billing = query.billingFor(db, id);
  const lim = daysUntil(m.limitationDate);

  const metrics = [
    { label: "Days to Limitation", value: lim != null ? (lim >= 0 ? lim : "Expired") : "—" },
    { label: "Next Hearing", value: formatDate(m.nextHearing, "dd MMM") },
    { label: "Tasks Due", value: tasks.filter((t) => t.status !== "Completed").length },
    { label: "Documents", value: docs.length },
    { label: "Outstanding Requests", value: financeTotals ? ledgerMoney(financeTotals.outstanding) : "—" },
  ];

  const quickActions = [
    { icon: Plus, label: "New Task", type: "task" },
    { icon: Upload, label: "Upload", type: "document" },
    { icon: Gavel, label: "Add Hearing", type: "hearing" },
    { icon: PenLine, label: "Draft", type: "draft" },
    { icon: Receipt, label: "Billing", type: "billing" },
  ];

  return (
    <div>
      {closureAction && can("matter.close") && <MatterClosure matter={m} action={closureAction} onClose={() => { setClosureAction(null); searchParams.delete("action"); setSearchParams(searchParams, { replace: true }); }} onSaved={() => { setClosureAction(null); searchParams.delete("action"); setSearchParams(searchParams, { replace: true }); reloadSnap(); toast.success("Matter status change recorded."); }} />}
      {/* Header */}
      <div className="mb-2 text-xs text-ink-muted">
        <button onClick={() => navigate("/matters")} className="hover:text-copper">Matters</button> / {m.caseNumber}
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-line pb-5">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-semibold text-ink">{m.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-2">
            <span className="rounded-full bg-copper-light px-2.5 py-0.5 text-xs font-medium text-copper">{m.practiceArea}</span>
            <span>{m.caseNumber}</span><span className="text-line-strong">·</span>
            <span>{m.court}</span><span className="text-line-strong">·</span>
            <span>{m.stage}</span>
            {m.representationMode && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-ink-2" data-testid="matter-representation">{m.representationMode.replaceAll("_", " ").toLowerCase()}{m.representedEntity ? ` · ${m.representedEntity}` : ""}</span>}
            <StatusBadge status={m.status} />
            {m.legalHold?.active && <span className="flex items-center gap-1 rounded-full bg-danger/10 px-2.5 py-0.5 text-xs font-medium text-danger" data-testid="matter-legal-hold-badge"><Lock className="h-3 w-3" /> Legal Hold</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setEditOpen(true)} data-testid="edit-matter-btn">Edit Matter</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled data-testid="archive-matter-unavailable">Archiving unavailable — use recorded closure</DropdownMenuItem>
              {can("matter.close") && (["Closed", "Disposed", "Archived"].includes(m.status) ? <DropdownMenuItem onClick={() => setClosureAction("reopen")} data-testid="reopen-matter">Reopen Matter</DropdownMenuItem> : <DropdownMenuItem onClick={() => setClosureAction("close")} data-testid="close-matter">Close Matter (with checks)</DropdownMenuItem>)}
              <DropdownMenuItem onClick={() => { update("matters", id, { priority: m.priority === "High" ? "Medium" : "High" }); toast.success("Priority updated."); }}>Toggle Priority</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        {!isClient && (
          <button onClick={showMissing} data-testid="quick-action-proofgraph"
            className="flex items-center gap-2 rounded-lg border border-copper/40 bg-copper-light px-3 py-1.5 text-sm font-medium text-copper transition-colors hover:border-copper">
            <GitBranch className="h-4 w-4" /> Show Me What’s Missing
          </button>
        )}
        {!isClient && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="quick-action-draft" className="flex items-center gap-2 rounded-lg border border-navy/30 bg-navy px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-navy/90"><PenLine className="h-4 w-4" /> Draft for this Matter</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => navigate(`/drafting?matterId=${id}`)} data-testid="draft-menu-new">New Draft</DropdownMenuItem>
              {db.drafts.filter((d) => d.matterId === id).slice(0, 5).map((d) => <DropdownMenuItem key={d.id} onClick={() => navigate(`/drafting/${d.id}?matterId=${id}`)} data-testid={`draft-menu-continue-${d.id}`}>Continue · {d.title}</DropdownMenuItem>)}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(`/drafting?matterId=${id}&from=order`)}>Draft from Court Order</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/drafting?matterId=${id}&from=issue`)}>Draft from Matter Issue</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeTab("Drafts")}>All drafts for this matter</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!isClient && <button onClick={() => navigate(`/ai-search?matterId=${id}`)} data-testid="quick-action-ask-ai" className="flex items-center gap-2 rounded-lg border border-line bg-card px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-copper/40 hover:text-ink"><Sparkles className="h-4 w-4" /> Research / Ask AI</button>}
        {quickActions.map((a) => (
          <button key={a.label} onClick={() => setQuick({ type: a.type })} data-testid={`quick-add-${a.type}`}
            className="flex items-center gap-2 rounded-lg border border-line bg-card px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-copper/40 hover:text-ink">
            <a.icon className="h-4 w-4" /> {a.label}
          </button>
        ))}
      </div>

      {/* Metrics */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {metrics.map((mt) => (
          <div key={mt.label} className={cn("rounded-xl border border-line bg-card p-3", mt.label === "Days to Limitation" && lim != null && lim <= 5 && "border-danger/40 bg-danger-bg")}>
            <div className="text-xs text-ink-2">{mt.label}</div>
            <div className="mt-1 font-display text-2xl font-semibold text-ink">{mt.value}</div>
            {mt.label === "Days to Limitation" && (
              <button
                onClick={() => { if (lim == null) { toast.error("No limitation date is set for this matter."); return; } setLimCheck((k) => k + 1); }}
                data-testid="check-limitation-btn"
                className="mt-2 flex items-center gap-1 text-[11px] font-medium text-copper transition-colors hover:text-copper/80">
                <Scale className="h-3 w-3" /> Check Limitation
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Attention bar (material alerts only) */}
      {!isClient && <AttentionBar snap={snap} onOpen={changeTab} />}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={changeTab} className="nyk-matter-tabs mt-6">
        <div className="flex items-start gap-4">
        <TabsList className="flex h-auto min-w-0 flex-1 flex-wrap items-start justify-start gap-x-5 gap-y-2 bg-transparent p-0">
          <LayoutGroup id="matter-tab-rail">
          {NAV_GROUPS.map((g) => {
            const tabs = g.tabs.filter((t) => visibleTabs.includes(t));
            if (!tabs.length) return null;
            return (
              <div key={g.label} className="flex flex-col gap-1" data-testid={`nav-group-${g.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="px-1 text-[10px] font-semibold uppercase tracking-widest text-ink-muted">{g.label}</div>
                <div className="flex flex-wrap gap-1">
                  {tabs.map((t) => {
                    const selected = tab === t;
                    return (
                      <TabsTrigger key={t} value={t} data-testid={`tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={cn(
                          "relative rounded-lg border border-transparent px-3 py-1.5 text-sm transition-colors data-[state=active]:shadow-none",
                          selected ? "text-copper" : "text-ink-2 hover:text-ink"
                        )}>
                        {selected && (
                          <motion.span
                            layoutId="matter-tab-active"
                            className="absolute inset-0 rounded-lg border border-line bg-card"
                            transition={SPRING.control}
                          />
                        )}
                        <span className="relative z-10">{TAB_LABEL[t] || t}</span>
                        {selected && (
                          <motion.span
                            layoutId="matter-tab-indicator"
                            className="absolute inset-x-2 -bottom-[3px] z-10 h-[2px] rounded-full bg-copper"
                            transition={SPRING.control}
                          />
                        )}
                      </TabsTrigger>
                    );
                  })}
                </div>
              </div>
            );
          })}
          </LayoutGroup>
        </TabsList>
        {limCheck > 0 && (
          <Panel className="w-[300px] shrink-0 p-3" data-testid="limitation-assessment">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-display text-sm font-semibold text-ink">Limitation Assessment</h3>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setLimCheck((k) => k + 1)} data-testid="limitation-recheck-btn">Re-check</Button>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setLimCheck(0)} data-testid="limitation-close-btn">Close</Button>
              </div>
            </div>
            <LimitationClockMark key={limCheck} limitationDate={m.limitationDate} daysLeft={lim} />
          </Panel>
        )}
        </div>

        <div className="mt-4">
          <TabsContent value="Snapshot">{isClient ? <SnapshotTab m={m} client={client} tasks={tasks} docs={docs} hearings={hearings} financeTotals={financeTotals} /> : <><SnapshotCommandCentre snap={snap} onOpen={changeTab}><p className="mt-3 text-sm text-ink-2">{m.description}</p></SnapshotCommandCentre><div className="mt-4"><IntelInbox matterId={id} onOpen={changeTab} /></div></>}</TabsContent>
          <TabsContent value="Record"><CasePassport matter={m} /></TabsContent>
          <TabsContent value="Research"><Panel className="p-6 text-sm text-ink-2">Research for this matter lives in the Research workspace with matter context carried over. <Button variant="link" className="px-1" onClick={() => navigate(`/research?matterId=${id}`)} data-testid="open-research">Open Research</Button></Panel></TabsContent>
          <TabsContent value="Drafts"><DraftsTab matterId={id} navigate={navigate} /></TabsContent>
          <TabsContent value="Evidence"><div className="space-y-4">{!isClient && <ChainOfTitlePanel matterId={id} />}<EvidenceTab matter={m} docs={docs} /></div></TabsContent>
          <TabsContent value="Proceedings"><ProceedingsTab matter={m} /></TabsContent>
          <TabsContent value="ProofGraph"><ProofGraphTab matterId={id} intent={proofIntent} onOpenDocument={() => changeTab("Docs & Exhibits")} /></TabsContent>
          <TabsContent value="Hearings"><HearingsTab hearings={hearings} onAdd={() => setQuick({ type: "hearing" })} /></TabsContent>
          <TabsContent value="Tasks"><TasksTab tasks={tasks} onAdd={() => setQuick({ type: "task" })} /></TabsContent>
          <TabsContent value="Docs & Exhibits"><DocsTab docs={docs} onAdd={() => setQuick({ type: "document" })} /></TabsContent>
          <TabsContent value="Parties"><PartiesTab matterId={id} isClient={isClient} /></TabsContent>
          <TabsContent value="AI"><AISimPanel matter={m} /></TabsContent>
          <TabsContent value="Billing"><FinanceWorkspace matterId={m.id} compact /></TabsContent>
          <TabsContent value="Notes"><NotesTab matterId={id} /></TabsContent>
          <TabsContent value="Communication"><CommTab matterId={id} /></TabsContent>
          <TabsContent value="Orders & Compliance"><OrdersTab matterId={id} onTask={() => setQuick({ type: "task" })} /></TabsContent>
          <TabsContent value="Timeline"><TimelineTab m={m} hearings={hearings} docs={docs} billing={billing} onCheckLimitation={() => { setLimCheck((k) => k + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }} /></TabsContent>
          <TabsContent value="Court Fee"><CourtFeeTab m={m} /></TabsContent>
          <TabsContent value="Strategy"><StrategyTab matterId={id} /></TabsContent>
        </div>
      </Tabs>

      {quick && <QuickAddDialogs type={quick.type} context={{ matterId: id, clientId: m.clientId }} onClose={() => setQuick(null)} />}
      <EditMatterSheet open={editOpen} onOpenChange={setEditOpen} m={m} />
    </div>
  );
}

/* ---------- Snapshot ---------- */
function SnapshotTab({ m, client, tasks, docs, hearings, financeTotals }) {
  const { db } = useApp();
  const outstanding = financeTotals?.outstanding;
  const latest = hearings[0];
  const meta = [
    ["Client", client?.name], ["Judge / Bench", m.judge], ["Opponent Counsel", m.opponentCounsel],
    ["Counsel", m.assignedTo.map((u) => query.userName(db, u)).join(", ")], ["Matter Value", m.matterValue ? formatMoney(m.matterValue) : "—"],
    ["Jurisdiction", m.jurisdiction], ["E-filing Number", m.efilingNumber], ["Fee Arrangement", m.feeArrangement],
  ];
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Panel className="p-4">
          <h3 className="font-display text-lg font-semibold text-ink">Matter Overview</h3>
          <p className="mt-1.5 text-sm text-ink-2">{m.description}</p>
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
            {meta.map(([k, v]) => (
              <div key={k}><div className="text-xs text-ink-muted">{k}</div><div className="text-ink">{v || "—"}</div></div>
            ))}
          </div>
        </Panel>
        <Panel>
          <PanelHeader title="Matter Intelligence" />
          <div className="p-4 text-sm text-ink-2" data-testid="matter-intelligence-empty">
            <p>No record-grounded summary is available yet. Matter Intelligence is generated only from this matter's verified record — documents, hearings, orders and notes — and is not shown until a grounded result exists.</p>
            <div className="mt-2 text-xs text-ink-muted">Open the <span className="font-medium text-ink">AI</span> tab to run a grounded brief against the current record.</div>
          </div>
        </Panel>
      </div>
      <div className="space-y-4">
        <Panel className="p-4">
          <h3 className="font-display text-base font-semibold text-ink">Immediate Attention</h3>
          <ul className="mt-2 space-y-1.5 text-sm text-ink-2">
            {daysUntil(m.limitationDate) != null && daysUntil(m.limitationDate) <= 7 && <li className="text-danger">• Limitation in {daysUntil(m.limitationDate)} days</li>}
            {tasks.filter((t) => t.status !== "Completed").slice(0, 2).map((t) => <li key={t.id}>• {t.title}</li>)}
            {outstanding > 0 && <li data-testid="matter-snapshot-outstanding">Outstanding requests {ledgerMoney(outstanding)}</li>}
          </ul>
        </Panel>
        <Panel className="p-4">
          <h3 className="font-display text-base font-semibold text-ink">Latest Hearing</h3>
          {latest ? <div className="mt-2 text-sm"><div className="text-ink">{formatDate(latest.date)} · {latest.purpose}</div><div className="text-xs text-ink-2">{latest.outcome || "Upcoming"}</div></div> : <div className="mt-2 text-sm text-ink-2">No hearings yet.</div>}
        </Panel>
        <Panel className="p-4">
          <h3 className="font-display text-base font-semibold text-ink">Financial Snapshot</h3>
          <div className="mt-2 space-y-1 text-sm">
            <Row k="Issued Requests" v={financeTotals ? ledgerMoney(financeTotals.billed) : '—'} />
            <Row k="Client Advance" v={financeTotals ? ledgerMoney(financeTotals.advance) : '—'} />
            <Row k="Outstanding" v={financeTotals ? ledgerMoney(outstanding) : '—'} />
          </div>
        </Panel>
      </div>
    </div>
  );
}
const Row = ({ k, v }) => <div className="flex justify-between"><span className="text-ink-2">{k}</span><span className="font-medium text-ink">{v}</span></div>;

/* ---------- Drafts (inventory from Drafting Studio) ---------- */
function DraftsTab({ matterId, navigate }) {
  const { db } = useApp();
  const drafts = (db.drafts || []).filter((d) => d.matterId === matterId);
  if (!drafts.length) return <EmptyState icon={PenLine} title="No drafts for this matter" message="Create a draft in the Drafting Studio with matter context." actionLabel="Open Drafting Studio" onAction={() => navigate("/drafting")} />;
  return (
    <Panel>
      <table className="w-full text-sm"><thead><tr className="border-b border-line bg-surface text-left text-xs uppercase tracking-wide text-ink-muted"><th className="px-4 py-2 font-medium">Document</th><th className="px-4 py-2 font-medium">Type</th><th className="px-4 py-2 font-medium">Version</th><th className="px-4 py-2 font-medium">Review Stage</th><th className="px-4 py-2 font-medium">Modified</th></tr></thead>
        <tbody>{drafts.map((d) => <tr key={d.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface" onClick={() => navigate(`/drafting/${d.id}`)} data-testid={`draft-row-${d.id}`}><td className="px-4 py-2.5 font-medium text-ink">{d.title}</td><td className="px-4 py-2.5 text-ink-2">{d.type}</td><td className="px-4 py-2.5 text-ink-2">v{d.version || 1}</td><td className="px-4 py-2.5"><StatusBadge status={d.status} /></td><td className="px-4 py-2.5 text-ink-2">{fromNow(d.updatedAt || d.createdAt)}</td></tr>)}</tbody></table>
    </Panel>
  );
}

/* ---------- Hearings ---------- */
function HearingsTab({ hearings, onAdd }) {
  const { update, ai } = useApp();
  const [open, setOpen] = useState(null);
  const [outcome, setOutcome] = useState("");
  const [order, setOrder] = useState("");
  const [saving, setSaving] = useState(false);
  const openHearing = (h) => { setOpen(h); setOutcome(h.outcome || ""); setOrder(h.order || ""); };
  const saveOutcome = async () => {
    if (!outcome.trim()) { toast.error("Enter the outcome first."); return; }
    setSaving(true);
    const r = await ai.classifyOutcome({ outcome, order });
    update("hearings", open.id, { status: "Completed", outcome, order: order || null, outcomeTag: r?.ai ? r.tag : null, outcomeConfidence: r?.confidence ?? null });
    setSaving(false);
    toast.success(r?.ai ? `Outcome saved · tagged ${r.tag}.` : "Outcome saved.");
    setOpen(null);
  };
  if (!hearings.length) return <EmptyState icon={Gavel} title="No hearings recorded" message="Add hearing dates to build the matter's procedural timeline." actionLabel="Add Hearing" onAction={onAdd} />;
  return (
    <>
      <div className="mb-3 flex justify-end"><Button size="sm" onClick={onAdd}>+ Add Hearing</Button></div>
      <div className="space-y-3">
        {hearings.map((h) => (
          <button key={h.id} onClick={() => openHearing(h)} className="flex w-full gap-4 rounded-xl border border-line bg-card p-4 text-left hover:border-copper/40" data-testid={`hearing-item-${h.id}`}>
            <div className="w-20 shrink-0"><div className="font-display text-lg font-semibold text-copper">{formatDate(h.date, "dd MMM")}</div><div className="text-xs text-ink-muted">{formatDate(h.date, "yyyy")}</div></div>
            <div className="flex-1"><div className="font-medium text-ink">{h.purpose}</div><div className="text-xs text-ink-2">{h.court} · {h.judge}</div><div className="mt-1 text-sm text-ink-2">{h.outcome || "Upcoming"}</div></div>
            <div className="flex flex-col items-end gap-1">
              {h.outcome ? <StatusBadge status="Completed" /> : <StatusBadge status="Pending" />}
              {h.outcomeTag && <span className="rounded-full bg-copper-light px-2 py-0.5 text-[11px] font-medium text-copper" data-testid={`hearing-tag-${h.id}`}>{h.outcomeTag}</span>}
            </div>
          </button>
        ))}
      </div>
      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent className="w-[460px] overflow-y-auto nyk-scroll">
          {open && (
            <>
              <SheetHeader><SheetTitle className="font-display text-2xl">{open.purpose}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-3 text-sm">
                <Detail k="Date" v={formatDateTime(open.date)} /><Detail k="Court" v={open.court} /><Detail k="Bench / Judge" v={open.judge} />
                <Detail k="Attendance" v={open.attendance} />
                {open.outcomeTag && <Detail k="Classification" v={<span className="rounded-full bg-copper-light px-2 py-0.5 text-xs font-medium text-copper">{open.outcomeTag}</span>} />}
                <div className="rounded-lg border border-line p-3"><div className="text-xs font-medium text-ink-2">Pre-hearing preparation</div><p className="mt-1 text-ink-2">{open.notes}</p></div>
                <div><div className="mb-1 text-xs text-ink-muted">Outcome</div><Textarea rows={2} value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="e.g. Interim stay granted; status quo on re-tender" data-testid="hearing-outcome-input" /></div>
                <div><div className="mb-1 text-xs text-ink-muted">Order / Direction</div><Textarea rows={2} value={order} onChange={(e) => setOrder(e.target.value)} placeholder="Operative part of the order" data-testid="hearing-order-input" /></div>
                <Button onClick={saveOutcome} disabled={saving} data-testid="save-outcome-btn">{saving ? "Classifying…" : "Save Outcome"}</Button>
                <div className="rounded-lg border border-line p-3"><div className="text-xs font-medium text-ink-2">Hearing media — order copies, photos, audio notes</div><AttachmentPanel matterId={open.matterId} attachedTo={{ collection: "hearings", id: open.id }} compact /></div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
const Detail = ({ k, v }) => <div><div className="text-xs text-ink-muted">{k}</div><div className="text-ink">{v}</div></div>;

function QuarantineBanner({ doc, onReleased }) {
  const { can, update } = useApp();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const release = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/documents/${doc.id}/quarantine/release`, { reason });
      update("documents", doc.id, { scanStatus: data.scanStatus, status: data.status }, { local: true });
      onReleased(data); toast.success("File released from quarantine.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not release file"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-lg border border-danger/40 bg-danger-bg p-3 text-xs" data-testid="quarantine-banner">
      <div className="font-semibold text-danger">Quarantined at upload — not available to the team</div>
      <ul className="mt-1 list-disc pl-4 text-ink-2">{(doc.scanReasons || []).map((r) => <li key={r}>{r}</li>)}</ul>
      <div className="mt-1 text-ink-muted">Engine: {doc.scanEngine}. OCR, classification and sharing are blocked until an administrator releases or deletes it.</div>
      {can("document.delete") && (
        <div className="mt-2 flex gap-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for release (min 10 chars)" className="h-8 text-xs" data-testid="quarantine-reason" />
          <Button size="sm" variant="outline" disabled={busy || reason.trim().length < 10} onClick={release} data-testid="quarantine-release-btn">Release</Button>
        </div>
      )}
    </div>
  );
}

/* ---------- Tasks ---------- */
function TasksTab({ tasks, onAdd }) {
  const { update } = useApp();
  const [view, setView] = useState("kanban");
  const cols = ["To Do", "In Progress", "Review", "Completed"];
  if (!tasks.length) return <EmptyState icon={Plus} title="You're all caught up." message="No tasks for this matter. Create one to get started." actionLabel="New Task" onAction={onAdd} />;
  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-1">
          {["kanban", "list"].map((v) => <button key={v} onClick={() => setView(v)} className={cn("rounded-md px-3 py-1 text-xs font-medium capitalize", view === v ? "bg-navy text-white" : "border border-line text-ink-2")}>{v}</button>)}
        </div>
        <Button size="sm" onClick={onAdd}>+ New Task</Button>
      </div>
      {view === "kanban" ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          {cols.map((c) => (
            <div key={c} className="rounded-xl border border-line bg-surface p-2">
              <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-ink-2">{c} · {tasks.filter((t) => t.status === c).length}</div>
              <div className="space-y-2">
                {tasks.filter((t) => t.status === c).map((t) => (
                  <div key={t.id} className="rounded-lg border border-line bg-card p-3" data-testid={`task-card-${t.id}`}>
                    <div className="text-sm font-medium text-ink">{t.title}</div>
                    <div className="mt-1 flex items-center justify-between text-xs text-ink-2"><span>{formatDate(t.dueDate, "dd MMM")}</span><StatusBadge status={t.priority} /></div>
                    <div className="mt-2 flex gap-1">
                      {cols.filter((x) => x !== c).map((x) => <button key={x} onClick={() => { update("tasks", t.id, { status: x }); toast.success(`Moved to ${x}.`); }} className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-ink-2 hover:bg-muted">{x}</button>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Panel>
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center justify-between border-b border-line px-4 py-2.5 last:border-0">
              <div><div className="text-sm font-medium text-ink">{t.title}</div><div className="text-xs text-ink-2">{t.category} · Due {formatDate(t.dueDate, "dd MMM")}</div></div>
              <div className="flex items-center gap-2"><StatusBadge status={t.priority} /><StatusBadge status={t.status} /></div>
            </div>
          ))}
        </Panel>
      )}
    </>
  );
}

/* ---------- Docs ---------- */
function DocsTab({ docs, onAdd }) {
  const { db, remove, fileUrl, can } = useApp();
  const [preview, setPreview] = useState(null);
  if (!docs.length) return <EmptyState icon={FileText} title="Upload documents to begin building the matter record." message="Pleadings, evidence, orders and correspondence live here." actionLabel="Upload Document" onAction={onAdd} />;
  return (
    <>
      <div className="mb-3 flex justify-end"><Button size="sm" onClick={onAdd}><Upload className="mr-1 h-3.5 w-3.5" /> Upload</Button></div>
      <Panel>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line bg-surface text-left text-xs uppercase tracking-wide text-ink-muted"><th className="px-4 py-2 font-medium">File</th><th className="px-4 py-2 font-medium">Category</th><th className="px-4 py-2 font-medium">Uploaded By</th><th className="px-4 py-2 font-medium">Date</th><th className="px-4 py-2 font-medium">Ver.</th><th className="px-4 py-2 font-medium">Status</th><th className="px-4 py-2" /></tr></thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface" onClick={() => setPreview(d)} data-testid={`doc-row-${d.id}`}>
                <td className="px-4 py-2.5 font-medium text-ink">{React.createElement(kindIcon(d.kind), { className: "mr-2 inline h-4 w-4 text-copper" })}{d.name}{!d.storagePath && <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-ink-muted">demo</span>}{d.attachedTo && <span className="ml-2 rounded bg-copper-light px-1.5 py-0.5 text-[10px] text-copper">{d.attachedTo.collection.replace(/s$/, "")} attachment</span>}</td>
                <td className="px-4 py-2.5 text-ink-2">{d.category}</td>
                <td className="px-4 py-2.5 text-ink-2">{query.userName(db, d.uploadedBy)}</td>
                <td className="px-4 py-2.5 text-ink-2">{formatDate(d.date)}</td>
                <td className="px-4 py-2.5 text-ink-2">v{d.version}</td>
                <td className="px-4 py-2.5"><StatusBadge status={d.status} /></td>
                <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}><button className="text-xs text-danger hover:underline" onClick={() => remove("documents", d.id, d.name)} data-testid={`doc-delete-${d.id}`}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Sheet open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <SheetContent className="w-[960px] max-w-full p-0">
          <SheetTitle className="sr-only">Document preview</SheetTitle>
          {preview && (
            <div className="grid h-full grid-cols-5">
              <div className="col-span-3 flex flex-col bg-ink/90 text-white/70"><MediaPreview doc={preview} /></div>
              <div className="col-span-2 flex flex-col overflow-hidden p-4">
                <h3 className="font-display text-xl font-semibold text-ink">Metadata</h3>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm"><Detail k="Category" v={preview.category} /><Detail k="Version" v={`v${preview.version}`} /><Detail k="Size" v={preview.size} /><Detail k="Status" v={preview.status} /></div>
                {preview.aiClassified && <div className="mt-3 rounded-lg border border-copper/30 bg-copper-light p-2.5 text-xs text-ink-2">Jev filed this under <b>{preview.aiCategory}</b> ({Math.round((preview.aiConfidence || 0) * 100)}% confidence).</div>}
                {preview.scanStatus === "QUARANTINED" && <QuarantineBanner doc={preview} onReleased={(d) => setPreview((p) => ({ ...p, ...d }))} />}
                <div className="mt-3 flex flex-wrap gap-2">
                  {preview.storagePath && <Button variant="outline" size="sm" asChild><a href={fileUrl(preview.id)} target="_blank" rel="noreferrer" data-testid="doc-open-btn">Open in new tab</a></Button>}
                  {preview.storagePath && <Button variant="outline" size="sm" asChild><a href={`${fileUrl(preview.id)}&download=1`} data-testid="doc-download-btn"><Download className="mr-1 h-3.5 w-3.5" />Download</a></Button>}
                  <Button variant="outline" size="sm" className="text-danger" onClick={() => { remove("documents", preview.id, preview.name); setPreview(null); }}>Move to Trash</Button>
                </div>
                {preview.storagePath && ['pdf', 'image'].includes(preview.kind) && <DocumentOCR doc={preview} onDone={(e) => setPreview(p => ({...p, category: e.category || p.category, ocrStatus: e.status}))} />}
                {preview.storagePath && <DocClassificationPanel doc={preview} matterId={preview.matterId} />}
                {preview.storagePath && can("matter.edit") && !preview.orderId && <Button variant="outline" size="sm" className="mt-2" onClick={async () => { try { await axios.post(`${API_BASE}/matters/${preview.matterId}/orders/from-document`, { documentId: preview.id, type: "Other" }); toast.success("Order record created — Order Agent is analysing it (see Orders tab)."); setPreview((p) => ({ ...p, orderId: "pending" })); } catch (e) { toast.error(e?.response?.data?.detail || "Could not create order"); } }} data-testid="doc-make-order-btn">Treat as Court Order → analyse</Button>}
                {preview.storagePath && preview.kind !== "video" && preview.kind !== "audio" ? (
                  <div className="mt-4 flex min-h-0 flex-1 flex-col border-t border-line pt-3" data-testid="doc-qa">
                    <div className="mb-2 font-display text-base font-semibold text-ink">Ask this document</div>
                    <AIChat key={preview.id} compact context={{ mode: "document", documentId: preview.id }} placeholder="e.g. What relief is prayed for?"
                      suggestions={["Summarise this document in 3 bullets", "List every date mentioned", "What are the key admissions?"]} />
                  </div>
                ) : (
                  <div className="mt-4 rounded-lg border border-dashed border-line p-3 text-xs text-ink-muted">{preview.storagePath ? "Document Q&A is available for text documents (PDF)." : "Document Q&A is available for uploaded files."}</div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

/* ---------- Parties ---------- */
function PartiesTab({ matterId, isClient }) {
  const { db, reload } = useApp();
  const parties = query.partiesFor(db, matterId);
  const builder = !isClient && <CauseTitleBuilder matterId={matterId} onChanged={() => reload?.()} />;
  if (!parties.length) return <div className="space-y-4">{builder}<EmptyState icon={User} title="No parties added" message="Add petitioners, respondents, witnesses and counsel." /></div>;
  const groups = parties.reduce((acc, p) => { const key = p.role.includes("Petitioner") || p.role.includes("Plaintiff") || p.role.includes("Complainant") ? "Petitioners" : p.role.includes("Respondent") || p.role.includes("Defendant") || p.role.includes("Accused") ? "Respondents" : p.role.includes("Witness") ? "Witnesses" : "Others"; (acc[key] = acc[key] || []).push(p); return acc; }, {});
  return (
    <div className="space-y-4">{builder}<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {Object.entries(groups).map(([g, list]) => (
        <Panel key={g}><PanelHeader title={g} /><div className="divide-y divide-line">
          {list.map((p) => (
            <div key={p.id} className="flex items-start gap-3 p-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-copper-light text-xs font-semibold text-copper">{initials(p.name)}</div>
              <div className="flex-1 text-sm"><div className="font-medium text-ink">{p.name}</div><div className="text-xs text-ink-2">{p.role}{p.contact ? ` · ${p.contact}` : ""}</div><div className="text-xs text-ink-muted">{[p.parentage, p.age && `aged ${p.age}`, p.address].filter(Boolean).join(" · ")}</div>{p.counsel && p.counsel !== "—" && <div className="text-xs text-ink-2">Counsel: {p.counsel}</div>}</div>
            </div>
          ))}
        </div></Panel>
      ))}
    </div></div>
  );
}

const Stat = ({ k, v, tone }) => <div className="rounded-xl border border-line bg-card p-3"><div className="text-xs text-ink-2">{k}</div><div className={cn("mt-1 font-display text-xl font-semibold", tone === "danger" ? "text-danger" : "text-ink")}>{v}</div></div>;

/* ---------- Notes ---------- */
function NotesTab({ matterId }) {
  const { db, create, remove, update } = useApp();
  const notes = query.notesFor(db, matterId);
  const [body, setBody] = useState("");
  const [type, setType] = useState("Private Note");
  const add = () => { if (!body.trim()) return; create("notes", { matterId, type, body, pinned: false, date: new Date().toISOString() }); setBody(""); toast.success("Note added."); };
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="lg:col-span-1"><Panel className="p-4">
        <div className="mb-2 flex flex-wrap gap-1">{["Private Note", "Team Note", "Strategy Note", "Hearing Note", "Research Note"].map((t) => <button key={t} onClick={() => setType(t)} className={cn("rounded-full px-2 py-0.5 text-[11px]", type === t ? "bg-copper text-white" : "bg-secondary text-ink-2")}>{t.replace(" Note", "")}</button>)}</div>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a note…" rows={4} data-testid="note-input" />
        <Button className="mt-2 w-full" size="sm" onClick={add} data-testid="add-note-btn">Add Note</Button>
      </Panel></div>
      <div className="space-y-3 lg:col-span-2">
        {notes.length === 0 ? <EmptyState icon={StickyNote} title="No notes yet" message="Capture private, team and strategy notes here." /> :
          notes.map((n) => (
            <Panel key={n.id} className="p-4">
              <div className="flex items-center justify-between"><span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-ink-2">{n.type}</span>
                <div className="flex gap-1"><button onClick={() => update("notes", n.id, { pinned: !n.pinned })} title="Pin"><Pin className={cn("h-3.5 w-3.5", n.pinned ? "text-copper" : "text-ink-muted")} /></button><button onClick={() => { remove("notes", n.id, "Note"); }} className="text-xs text-danger">Delete</button></div>
              </div>
              <p className="mt-2 text-sm text-ink">{n.body}</p><div className="mt-1 text-xs text-ink-muted">{query.userName(db, n.author)} · {fromNow(n.date)}</div>
              <AttachmentPanel matterId={matterId} attachedTo={{ collection: "notes", id: n.id }} compact />
            </Panel>
          ))}
      </div>
    </div>
  );
}

/* ---------- Communication ---------- */
function CommTab({ matterId }) {
  const { db, create } = useApp();
  const comms = query.commsFor(db, matterId);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ channel: "Email", subject: "", body: "" });
  const send = () => { create("communications", { matterId, channel: f.channel, subject: f.subject, body: f.body, date: new Date().toISOString(), direction: "Outgoing", status: "Sent" }); toast.success("Communication logged (not actually sent)."); setOpen(false); setF({ channel: "Email", subject: "", body: "" }); };
  return (
    <>
      <div className="mb-3 flex justify-end"><Button size="sm" onClick={() => setOpen(true)}>Compose</Button></div>
      {comms.length === 0 ? <EmptyState icon={User} title="No communications logged" /> :
        <div className="space-y-2">{comms.map((c) => (
          <Panel key={c.id} className="flex items-start gap-3 p-3">
            <span className="rounded-full bg-info-bg px-2 py-0.5 text-[11px] text-info">{c.channel}</span>
            <div className="flex-1"><div className="text-sm font-medium text-ink">{c.subject}</div><div className="text-xs text-ink-2">{c.body}</div><div className="text-xs text-ink-muted">{fromNow(c.date)} · {c.status}</div></div>
          </Panel>
        ))}</div>}
      <Sheet open={open} onOpenChange={setOpen}><SheetContent><SheetHeader><SheetTitle className="font-display text-2xl">Compose</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-3">
          <div className="flex gap-1">{["Email", "WhatsApp", "SMS", "Call", "Meeting"].map((ch) => <button key={ch} onClick={() => setF({ ...f, channel: ch })} className={cn("rounded-full px-2.5 py-1 text-xs", f.channel === ch ? "bg-copper text-white" : "bg-secondary text-ink-2")}>{ch}</button>)}</div>
          <Input placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
          <Textarea placeholder="Message…" rows={5} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
          <div className="rounded-lg bg-warning-bg px-3 py-2 text-xs text-warning">Prototype only — external communications are simulated, not sent.</div>
          <Button className="w-full" onClick={send}>Save as Sent</Button>
        </div>
      </SheetContent></Sheet>
    </>
  );
}

/* ---------- Orders & Compliance ---------- */
function OrdersTab({ matterId, onTask }) {
  const { db, update, create, pushNotification } = useApp();
  const orders = query.ordersFor(db, matterId);
  const [addOpen, setAddOpen] = useState(false);
  const [f, setF] = useState({ summary: "", directions: "", date: "", complianceDeadline: "", responsible: "u_kesav" });
  const addOrder = () => {
    if (!f.summary.trim()) { toast.error("Order summary is required."); return; }
    create("orders", { matterId, court: query.matter(db, matterId)?.court || "—", date: f.date || new Date().toISOString(), summary: f.summary, directions: f.directions, complianceDeadline: f.complianceDeadline || null, responsible: f.responsible, status: "In Progress", documentId: null });
    if (f.complianceDeadline) {
      create("tasks", { matterId, title: `Comply: ${f.summary.slice(0, 50)}`, assignee: f.responsible, priority: "High", status: "To Do", dueDate: f.complianceDeadline, category: "Compliance", description: f.directions || "Auto-created from court order." });
      pushNotification({ category: "Orders", title: "Order compliance created", body: `${f.summary} — compliance task & calendar deadline set for ${formatDate(f.complianceDeadline)}.`, matterId });
      toast.success("Order added — compliance task & calendar deadline created.");
    } else {
      toast.success("Order added.");
    }
    setAddOpen(false); setF({ summary: "", directions: "", date: "", complianceDeadline: "", responsible: "u_kesav" });
  };
  return (
    <>
    <div className="mb-3 flex justify-end"><Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-order-btn">+ Add Order</Button></div>
    {!orders.length ? <EmptyState icon={Scale} title="No orders recorded" message="Court orders and their compliance obligations appear here." /> :
    <div className="space-y-3">{orders.map((o) => {
      const d = daysUntil(o.complianceDeadline); const overdue = d != null && d < 0 && o.status !== "Completed";
      return (
        <Panel key={o.id} className={cn("p-4", overdue && "border-danger/40")}>
          <div className="flex items-start justify-between"><div><div className="text-sm font-medium text-ink">{o.summary}</div><div className="text-xs text-ink-2">{o.court} · {formatDate(o.date)}</div></div><StatusBadge status={o.status} /></div>
          <p className="mt-2 text-sm text-ink-2">{o.directions}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className={cn(overdue ? "font-medium text-danger" : "text-ink-2")}>Compliance: {formatDate(o.complianceDeadline)} {overdue && "(Overdue)"}</span>
            <span className="text-ink-2">· Responsible: {query.userName(db, o.responsible)}</span>
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" size="sm" onClick={onTask}>Add Compliance Task</Button>
            <Button variant="outline" size="sm" onClick={() => { update("orders", o.id, { status: "Completed" }); toast.success("Marked complied."); }}>Mark Complied</Button>
          </div>
          <OrderAgentPanel order={o} docs={query.docsFor ? query.docsFor(db, matterId) : db.documents.filter((d) => d.matterId === matterId)} />
        </Panel>
      );
    })}</div>}
    <Sheet open={addOpen} onOpenChange={setAddOpen}><SheetContent className="w-[440px]"><SheetHeader><SheetTitle className="font-display text-2xl">Add Court Order</SheetTitle></SheetHeader>
      <div className="mt-4 space-y-3">
        <div><div className="text-xs text-ink-2">Order Summary *</div><Input value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="order-summary-input" /></div>
        <div><div className="text-xs text-ink-2">Directions</div><Textarea rows={3} value={f.directions} onChange={(e) => setF({ ...f, directions: e.target.value })} /></div>
        <div><div className="text-xs text-ink-2">Order Date</div><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
        <div><div className="text-xs text-ink-2">Compliance Deadline</div><Input type="date" value={f.complianceDeadline} onChange={(e) => setF({ ...f, complianceDeadline: e.target.value })} data-testid="order-compliance-input" /></div>
        <div className="rounded-lg bg-info-bg px-3 py-2 text-xs text-info">Setting a compliance deadline auto-creates a compliance task and a calendar deadline.</div>
        <Button className="w-full" onClick={addOrder} data-testid="save-order-btn">Save Order</Button>
      </div>
    </SheetContent></Sheet>
    </>
  );
}

/* ---------- Timeline ---------- */
function TimelineTab({ m, hearings, docs, billing, onCheckLimitation }) {
  const [filter, setFilter] = useState("All");
  const events = [
    { type: "Creation", label: "Matter created", date: m.createdAt },
    ...hearings.map((h) => ({ type: "Hearing", label: `${h.purpose} — ${h.outcomeTag ? `${h.outcomeTag}: ` : ""}${h.outcome || "scheduled"}`, date: h.date })),
    ...docs.map((d) => ({ type: "Document", label: `Uploaded ${d.name}`, date: d.date })),
    ...billing.map((b) => ({ type: "Legacy Billing", label: `Unverified historical record: ${b.type} — ${formatMoney(b.amount)}`, date: b.date })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));
  const types = ["All", "Hearing", "Document", "Payment", "Creation"];
  const shown = events.filter((e) => filter === "All" || e.type === filter);
  return (
    <div>
      {m.limitationDate && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2.5" data-testid="timeline-limitation">
          <span className="text-xs font-medium text-ink-2">Limitation status</span>
          <LimitationBadge limitationDate={m.limitationDate} onClick={onCheckLimitation} />
          <span className="text-xs text-ink-muted">· expires {formatDate(m.limitationDate)}</span>
          <button onClick={onCheckLimitation} className="ml-auto text-[11px] font-medium text-copper hover:text-copper/80" data-testid="timeline-check-limitation">Open assessment</button>
        </div>
      )}
      <div className="mb-3 flex flex-wrap gap-1.5">{types.map((t) => <button key={t} onClick={() => setFilter(t)} className={cn("rounded-full px-3 py-1 text-xs", filter === t ? "bg-navy text-white" : "border border-line text-ink-2")}>{t}</button>)}</div>
      <div className="relative border-l border-line pl-6">
        {shown.map((e, i) => (
          <div key={i} className="relative mb-5"><div className="absolute -left-[27px] top-1 h-2.5 w-2.5 rounded-full bg-copper" />
            <div className="text-sm font-medium text-ink">{e.label}</div><div className="text-xs text-ink-muted">{e.type} · {formatDate(e.date, "dd MMM yyyy")}</div></div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Court Fee ---------- */
function CourtFeeTab({ m }) {
  const { update } = useApp();
  const existing = m.courtFeeManual || null;
  const [amount, setAmount] = useState(existing?.amount ?? "");
  const [basis, setBasis] = useState(existing?.basis || "");
  const [saving, setSaving] = useState(false);
  const save = () => {
    const amt = Number(amount);
    if (!amt || amt < 0) { toast.error("Enter the court fee actually assessed for this matter."); return; }
    setSaving(true);
    update("matters", m.id, { courtFeeManual: { amount: amt, basis: basis.trim(), currency: "INR", recordedAt: new Date().toISOString() } });
    setTimeout(() => { setSaving(false); toast.success("Court fee recorded."); }, 300);
  };
  return (
    <Panel className="max-w-2xl p-5">
      <div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-copper" /><h3 className="font-display text-xl font-semibold text-ink">Court Fee & Valuation</h3></div>
      <div className="mt-3 rounded-lg border border-warning/30 bg-warning-bg px-3 py-2.5 text-xs text-warning" data-testid="courtfee-config-unavailable">
        Automatic court-fee computation is not available. Court fee depends on state legislation, the relief claimed, the valuation provision, forum, subject matter and applicable exemptions — the Rules Engine is not yet authoritative for this forum. Record the fee assessed by the court or computed manually below.
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div><div className="text-xs text-ink-2">Matter Type</div><Input value={m.type} disabled className="mt-1" /></div>
        <div><div className="text-xs text-ink-2">Jurisdiction</div><Input value={m.jurisdiction} disabled className="mt-1" /></div>
        <div><div className="text-xs text-ink-2">Court Fee Assessed (₹)</div><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter recorded amount" className="mt-1" data-testid="courtfee-manual-amount" /></div>
        <div><div className="text-xs text-ink-2">Basis / Provision (optional)</div><Input value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="e.g. Sch. I, AP Court Fees Act" className="mt-1" data-testid="courtfee-manual-basis" /></div>
      </div>
      <div className="mt-4 flex items-center justify-between rounded-lg border border-line bg-surface p-4">
        <span className="text-sm text-ink-2">Recorded court fee</span>
        <span className="font-display text-xl font-semibold text-copper" data-testid="courtfee-recorded">{existing ? formatMoney(existing.amount) : "Not recorded"}</span>
      </div>
      <div className="mt-3 flex justify-end"><Button size="sm" onClick={save} disabled={saving} data-testid="courtfee-save-btn">{saving ? "Saving…" : "Record court fee"}</Button></div>
    </Panel>
  );
}

/* ---------- Strategy (server-persisted, versioned, audited) ---------- */
const STRATEGY_SECTIONS = ["Case Theory", "Objectives", "Strong Points", "Weak Points", "Opponent Theory", "Potential Objections", "Evidence Plan", "Witness Plan", "Hearing Strategy", "Settlement Position", "Next Steps"];
function StrategyTab({ matterId }) {
  const { db } = useApp();
  const [data, setData] = useState({});
  const [meta, setMeta] = useState({ version: 0, updatedBy: null, updatedAt: null });
  const [state, setState] = useState("loading"); // loading | ready | error
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const load = React.useCallback(() => {
    setState("loading");
    axios.get(`${API_BASE}/matters/${matterId}/strategy`)
      .then((r) => { setData(r.data.sections || {}); setMeta({ version: r.data.version, updatedBy: r.data.updatedBy, updatedAt: r.data.updatedAt }); setState("ready"); })
      .catch(() => setState("error"));
  }, [matterId]);
  useEffect(() => { load(); }, [load]);
  const saveSection = async (s) => {
    const next = { ...data, [s]: draft };
    setSaving(true);
    try {
      const { data: res } = await axios.put(`${API_BASE}/matters/${matterId}/strategy`, { sections: next });
      setData(res.sections || next); setMeta({ version: res.version, updatedBy: res.updatedBy, updatedAt: res.updatedAt });
      setEditing(null); toast.success("Strategy saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save strategy — your change was not persisted.");
    } finally { setSaving(false); }
  };
  if (state === "loading") return <div className="p-6 text-sm text-ink-muted" data-testid="strategy-loading">Loading strategy…</div>;
  if (state === "error") return (
    <div className="p-2"><Panel className="max-w-md p-5 text-sm"><div className="text-danger" data-testid="strategy-error">Strategy could not be loaded. This is a connection error, not an empty strategy.</div><Button size="sm" variant="ghost" className="mt-2" onClick={load} data-testid="strategy-retry-btn">Retry</Button></Panel></div>
  );
  return (
    <div>
      <div className="mb-3 text-xs text-ink-muted" data-testid="strategy-meta">
        {meta.version > 0
          ? <span>Saved · v{meta.version}{meta.updatedBy ? ` · ${query.userName(db, meta.updatedBy)}` : ""}{meta.updatedAt ? ` · ${fromNow(meta.updatedAt)}` : ""}</span>
          : <span>Not yet defined — start capturing strategy below. Changes are saved to the matter record.</span>}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {STRATEGY_SECTIONS.map((s) => (
          <Panel key={s} className="p-4">
            <div className="flex items-center justify-between"><h4 className="font-display text-base font-semibold text-ink">{s}</h4>
              <button className="text-xs text-copper" data-testid={`strategy-edit-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => { setEditing(s); setDraft(data[s] || ""); }}>Edit</button></div>
            {editing === s ? (
              <div className="mt-2"><Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} data-testid="strategy-draft-input" />
                <div className="mt-2 flex gap-2"><Button size="sm" disabled={saving} onClick={() => saveSection(s)} data-testid="strategy-save-btn">{saving ? "Saving…" : "Save"}</Button><Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button></div></div>
            ) : (
              <p className="mt-1.5 text-sm text-ink-2">{data[s] || "Not yet defined."}</p>
            )}
          </Panel>
        ))}
      </div>
    </div>
  );
}

/* ---------- Edit Matter Sheet ---------- */
function EditMatterSheet({ open, onOpenChange, m }) {
  const { update } = useApp();
  const [f, setF] = useState(m);
  const [modes, setModes] = useState([]);
  React.useEffect(() => setF(m), [m]);
  React.useEffect(() => { axios.get(`${API_BASE}/matters/meta/passport`).then((r) => setModes(r.data.representationModes)).catch(() => {}); }, []);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const { insertLocal } = useApp();
  const save = async () => {
    setSaving(true); setSaveError("");
    try {
      const { data } = await axios.patch(`${API_BASE}/data/matters/${m.id}`, { title: f.title, stage: f.stage, judge: f.judge, opponentCounsel: f.opponentCounsel, nextHearing: f.nextHearing, expectedVersion: f.version || 1 });
      insertLocal("matters", data); setF((old) => ({ ...old, version: data.version }));
      if (f.representationMode && (f.representationMode !== m.representationMode || f.representedEntity !== m.representedEntity)) {
        const result = await axios.put(`${API_BASE}/matters/${m.id}/representation`, { representationMode: f.representationMode, representedEntity: f.representedEntity || null, confidentiality: f.confidentiality || "STANDARD" });
        if (result.data.id) insertLocal("matters", result.data);
      }
      toast.success("Matter changes saved."); onOpenChange(false);
    } catch (e) { const d = e.response?.data?.detail; setSaveError(typeof d === "string" ? d : d?.message || "Changes could not be saved. Your edits remain here."); }
    finally { setSaving(false); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}><SheetContent className="w-[460px] overflow-y-auto nyk-scroll" aria-describedby="edit-matter-desc"><SheetHeader><SheetTitle className="font-display text-2xl">Edit Matter</SheetTitle><p id="edit-matter-desc" className="text-xs text-ink-2">Title, stage, representation and schedule.</p></SheetHeader>
      <div className="mt-4 space-y-3">
        <div><div className="text-xs text-ink-2">Title</div><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="edit-title" /></div>
        <div><div className="text-xs text-ink-2">Representation</div><select className="h-9 w-full rounded-md border border-line bg-card px-2 text-sm" value={f.representationMode || ""} onChange={(e) => setF({ ...f, representationMode: e.target.value })} data-testid="edit-representation"><option value="">— not set —</option>{modes.map((x) => <option key={x} value={x}>{x.replaceAll("_", " ")}</option>)}</select></div>
        <div><div className="text-xs text-ink-2">Represented Entity</div><Input value={f.representedEntity || ""} onChange={(e) => setF({ ...f, representedEntity: e.target.value })} placeholder="e.g. State of Andhra Pradesh / Kurella Govindu" data-testid="edit-represented-entity" /></div>
        <div><div className="text-xs text-ink-2">Stage</div><Input value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })} /></div>
        <div><div className="text-xs text-ink-2">Judge / Bench</div><Input value={f.judge} onChange={(e) => setF({ ...f, judge: e.target.value })} /></div>
        <div><div className="text-xs text-ink-2">Opponent Counsel</div><Input value={f.opponentCounsel} onChange={(e) => setF({ ...f, opponentCounsel: e.target.value })} /></div>
        <div><div className="text-xs text-ink-2">Next Hearing</div><Input type="date" value={f.nextHearing ? f.nextHearing.slice(0, 10) : ""} onChange={(e) => setF({ ...f, nextHearing: e.target.value })} /></div>
        {saveError && <p role="alert" data-testid="edit-matter-error" className="text-sm text-danger">{saveError}</p>}
        <Button className="w-full" disabled={saving} onClick={save} data-testid="save-matter-btn">{saving ? "Saving…" : "Save Changes"}</Button>
      </div>
    </SheetContent></Sheet>
  );
}
